"use client"
import useMergedRef from "@react-hook/merged-ref"
import type React from "react"
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type Ref,
  useCallback,
  useRef,
  useState,
} from "react"
import { classNames } from "../../utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandItemAvatar,
  CommandItemContent,
  CommandItemDescription,
  CommandItemSide,
  CommandItemTitle,
  CommandList,
  type CommandProps,
  CommandShortcut,
} from "../Command"
import { Popover, type PopoverProps } from "../Popover"
import type { SelectButtonProps } from "../Select"

export type ComboboxProps = Omit<
  Pick<
    PopoverProps,
    | "open"
    | "defaultOpen"
    | "onOpenChange"
    | "onInteractOutside"
    | "onEscapeKeyDown"
    | "onOpenAutoFocus"
  > &
    Omit<SelectButtonProps, "value"> &
    CommandProps &
    Omit<ComponentPropsWithoutRef<typeof CommandInput>, "content"> & {
      content: React.ReactNode
      value?: string
      closeOnSelect?: boolean
      overrides?: {
        Popover?: Partial<PopoverProps>
        Input?: Partial<ComponentProps<typeof CommandInput>>
      }
    },
  "children" | "onChange"
>

function Combobox({
  open,
  onOpenChange,
  defaultOpen,
  content,
  filter,
  shouldFilter,
  label,
  onOpenAutoFocus,
  onFocus,
  onKeyDown,
  onInteractOutside,
  onEscapeKeyDown,
  closeOnSelect = true,
  overrides,
  ref,
  ...rest
}: ComboboxProps) {
  // Manually keep track of the opened state due to unique behavior of Combobox
  const [openedState, setOpenedState] = useState(defaultOpen ?? open)
  const resolvedOpenState = open === undefined ? openedState : open
  const inputRef = useRef<HTMLInputElement>(null)
  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      setOpenedState(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  const onSelect = useCallback(() => {
    if (closeOnSelect) {
      handleOnOpenChange(false)
    }
  }, [closeOnSelect, handleOnOpenChange])

  return (
    <Command
      filter={filter}
      label={label}
      onSelect={onSelect}
      shouldFilter={shouldFilter}
    >
      <Popover
        content={content}
        defaultOpen={defaultOpen}
        onEscapeKeyDown={e => {
          handleOnOpenChange(false)
          onEscapeKeyDown?.(e)
        }}
        onInteractOutside={e => {
          if (e.target === inputRef.current) {
            e.preventDefault()
            return
          }

          handleOnOpenChange(false)
          onInteractOutside?.(e)
        }}
        onOpenAutoFocus={e => {
          // Prevent popover from automatically being focused, as the Input should keep focus instead
          e.preventDefault()
          inputRef.current?.focus()
          onOpenAutoFocus?.(e)
        }}
        open={resolvedOpenState}
        sideOffset={4}
        {...overrides?.Popover}
        className={classNames("p-1", overrides?.Popover?.className)}
      >
        <div className={classNames("h-fit", rest.className)}>
          <CommandInput
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
              handleOnOpenChange(true)
              onFocus?.(e)
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "ArrowDown") {
                handleOnOpenChange(true)
              }
              onKeyDown?.(e)
            }}
            {...rest}
            {...overrides?.Input}
            ref={useMergedRef(ref as Ref<HTMLInputElement>, inputRef)}
          />
        </div>
      </Popover>
    </Command>
  )
}

export const ComboboxList = CommandList

export const ComboboxEmpty = CommandEmpty

const ComboboxItem = CommandItem

export const ComboboxItemAvatar = CommandItemAvatar

export const ComboboxItemContent = CommandItemContent

export const ComboboxItemTitle = CommandItemTitle

export const ComboboxItemDescription = CommandItemDescription

export const ComboboxItemSide = CommandItemSide

export const ComboboxShortcut = CommandShortcut

export const ComboboxGroup = CommandGroup

/**
 * A Combobox is an autocomplete input and command palette with a list of suggestions.
 * This opens a popover with the list when the input is focused.
 * For a Combobox that is self-contained within a Popover, see FloatingCombobox.
 */
export { Combobox, ComboboxItem }
